import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import {
  ChangePackageSettlementVm,
  ChangeRoute,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import {
  formatAbsoluteMoneyList,
  formatMoneyList,
  formatSignedMoneyList,
} from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import {
  isZeroMoneyList,
  moneyListDirection,
} from '@/features/dashboard/bookingChanges/utils/money.ts';
import {
  pluralizeInstallmentsLocative,
  pluralizeItems,
} from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/**
 * Napisy przepływu liczone z danych — bilans, dopiski odznak, zdania opisu
 * kroków i teksty stopki.
 *
 * Ekrany dostają je gotowe, więc żaden nie skleja zdania z liczby sam; dzięki
 * temu opis nad tabelą nie może rozjechać się z tym, co tabela pokazuje.
 */

/**
 * Wpływ pozycji na kwotę do pokazania. Zmiana bez skutku finansowego oddaje
 * pustą listę, czyli kreskę zamiast „0,00 zł” — w kolumnie kwot ma się rzucać
 * w oczy wyłącznie to, co rusza pieniądze; tak samo czyta się rachunek
 * uczestnika.
 */
export const summaryImpactValues = (delta: MoneyValueDto[]): MoneyValueDto[] =>
  isZeroMoneyList(delta) ? [] : delta;

/**
 * Nazwa bilansu paczki: dodatni to dopłata, ujemny zwrot, zero brak wpływu.
 * Używana w wierszu „Bilans paczki · dopłata” i w dopisku odznaki kroku.
 *
 * Paczka wielowalutowa potrafi jedną walutą dopłacać, a drugą zwracać — wtedy
 * kierunku nie ma i zdanie mówi o samej zmianie, zamiast wybierać walutę.
 */
export const balanceLabel = (balance: MoneyValueDto[]): string => {
  switch (moneyListDirection(balance)) {
    case 0:
      return 'bez wpływu na kwotę';
    case 1:
      return 'dopłata';
    case -1:
      return 'zwrot';
    default:
      return 'zmiana kwoty';
  }
};

/**
 * Zdanie zapowiadające krok rozliczenia. Rzeczownik idzie za kierunkiem
 * bilansu — paczka oddająca pieniądze nie zapowiada „dopłaty”.
 */
export const settlementIntroSentence = (balance: MoneyValueDto[]): string => {
  switch (moneyListDirection(balance)) {
    case 0:
      return 'Następny krok rozstrzyga, jak paczka wchodzi w życie.';
    case 1:
      return 'Następny krok rozstrzyga, jak dopłata wchodzi w harmonogram rat.';
    case -1:
      return 'Następny krok rozstrzyga, jak zwrot wchodzi w harmonogram rat.';
    default:
      return 'Następny krok rozstrzyga, jak zmiana kwoty wchodzi w harmonogram rat.';
  }
};

/**
 * Nagłówek bloku kwoty na stronie klienta (W3) — tam kwota stoi bez znaku,
 * a kierunek niesie sam napis.
 */
export const balanceHeading = (balance: MoneyValueDto[]): string => {
  switch (moneyListDirection(balance)) {
    case 0:
      return 'Bez zmiany kwoty';
    case 1:
      return 'Dopłata';
    case -1:
      return 'Zwrot';
    default:
      return 'Zmiana kwoty';
  }
};

/** Dopisek przy odznace kroku W1: „Paczka #13 · szkic”. */
export const packageContextLabel = (
  packageNumber: number,
  statusLabel: string,
): string =>
  `Paczka #${packageNumber} · ${statusLabel.toLocaleLowerCase('pl')}`;

/** Dopisek przy odznace kroku W2: „Paczka #13 · dopłata 240,00 zł”. */
export const packageBalanceContextLabel = (
  packageNumber: number,
  balance: MoneyValueDto[],
): string => {
  const direction = moneyListDirection(balance);
  if (direction === 0) {
    return `Paczka #${packageNumber} · ${balanceLabel(balance)}`;
  }
  // Kwota bez znaku tylko wtedy, gdy kierunek niesie sam napis; przy paczce
  // dopłacającej w jednej walucie i zwracającej w drugiej znak musi zostać.
  const amounts =
    direction === null
      ? formatSignedMoneyList(balance)
      : formatAbsoluteMoneyList(balance);
  return `Paczka #${packageNumber} · ${balanceLabel(balance)} ${amounts}`;
};

/**
 * Pierwsze zdanie opisu kroku W1 — ile pozycji niesie szkic i ile z nich rusza
 * kwotę. Liczone z danych, żeby opis nie rozjechał się z tabelą pod nim.
 */
export const summaryScopeSentence = (
  itemCount: number,
  financialItemCount: number,
): string => {
  if (itemCount === 0) {
    return 'Szkic nie ma jeszcze żadnej pozycji.';
  }
  if (financialItemCount === 0) {
    return `${pluralizeItems(itemCount)} w szkicu, żadna nie zmienia kwoty.`;
  }
  return `${pluralizeItems(itemCount)} w szkicu, w tym ${financialItemCount} ze skutkiem finansowym.`;
};

/**
 * Opis kroku W2: co już wpłacono, ile zostaje i o jakiej kwocie rozstrzyga
 * wybór poniżej.
 */
export const settlementScopeSentence = (
  settlement: ChangePackageSettlementVm,
): string => {
  const unpaidCount = settlement.adjustableCount;
  const absolute = formatAbsoluteMoneyList(settlement.balance);
  const schedulePart =
    unpaidCount === 0
      ? 'bez zaplanowanych rat'
      : `w ${pluralizeInstallmentsLocative(unpaidCount)}`;

  const state = `Wpłacono ${formatMoneyList(settlement.paid)}, do zapłaty zostaje ${formatMoneyList(
    settlement.dueBefore,
  )} ${schedulePart}.`;

  // Paczka bez skutku finansowego nie ma czego rozkładać na raty; zdanie o tym,
  // „gdzie ląduje", pokazywało wtedy myślnik w miejscu kwoty.
  return isZeroMoneyList(settlement.balance)
    ? `${state} Paczka nie zmienia kwoty, więc harmonogram zostaje bez zmian.`
    : `${state} Wybór poniżej decyduje, gdzie ląduje ${absolute}.`;
};

/**
 * Przypis pod wyborem trasy w stopce kroku W2.
 *
 * O terminie ważności linku nie ma tu ani słowa, bo takiego terminu nie ma:
 * paczka czekająca na klienta nie wygasa sama, a token rezerwacji żyje do
 * końca wyjazdu. Zdjąć ją może wyłącznie operator.
 */
export const settlementFootnote = (route: ChangeRoute): string =>
  route === ChangeRoute.CLIENT_ACCEPTANCE
    ? 'Paczka czeka na decyzję zapisującego i do tego czasu blokuje edycję rezerwacji. Bez jego reakcji zdejmiesz ją sam.'
    : 'Krok akceptacji zostaje pominięty, więc nie ma komu podpisać dokumentów — cofnięcie wymaga nowej paczki.';

/** Etykieta głównego przycisku kroku W2 — zależna od wybranej trasy. */
export const settlementCtaLabel = (route: ChangeRoute): string =>
  route === ChangeRoute.CLIENT_ACCEPTANCE
    ? 'Wyślij do akceptacji'
    : 'Wprowadź paczkę';
